import { useState, useEffect } from "react";

const TOUCH_QUERY = "(hover: none) and (pointer: coarse)";

/**
 * True on primarily-touch devices (phones, tablets). Hover-capable devices —
 * mouse-driven desktops, including a narrowed Chrome window and a laptop that
 * happens to have a touchscreen — return false.
 *
 * Deliberately a capability query, not a width breakpoint (`usePhoneViewport`):
 * the things that differ here are the ones a pointer implies — no hover state,
 * and an on-screen keyboard that eats half the screen when a field takes focus.
 * A 600px-wide desktop window has neither.
 *
 * Live: re-renders on rotation / window resize / moving the window between a
 * touchscreen and an external monitor, so state derived from it can't get stuck
 * in the mode the device had a moment ago.
 */
export function useIsTouchDevice() {
  const [isTouch, setIsTouch] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(TOUCH_QUERY).matches;
  });
  useEffect(() => {
    if (!window.matchMedia) return;
    const mql = window.matchMedia(TOUCH_QUERY);
    const handler = (e) => setIsTouch(e.matches);
    if (mql.addEventListener) {
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    }
    // Safari < 14 fallback
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, []);
  return isTouch;
}
